import type { AppConfigView } from "@/lib/api/types";

/**
 * Two-level taxonomy for the App configs page: a fixed list of sections, and
 * within the integration sections one group per carrier / gateway so a brand's
 * keys sit together instead of scattered through a flat list.
 *
 * Group labels are brand names (proper nouns) — deliberately not localized.
 * Section titles are i18n keys, resolved by the page.
 *
 * A registry key that matches no rule lands in "system"; custom keys always go
 * to "custom". Adding a carrier/gateway means one line in PREFIX_RULES.
 */

export type SectionId =
  | "shipping"
  | "payments"
  | "messaging"
  | "orders"
  | "ai"
  | "system"
  | "custom";

// Render order on the page.
export const SECTION_ORDER: readonly SectionId[] = [
  "shipping",
  "payments",
  "messaging",
  "orders",
  "ai",
  "system",
  "custom",
];

type PrefixRule = {
  readonly prefix: string;
  readonly section: SectionId;
  readonly group: string;
};

// First match wins, so a longer prefix must precede any prefix of itself.
const PREFIX_RULES: readonly PrefixRule[] = [
  { prefix: "BOSTA_", section: "shipping", group: "Bosta" },
  { prefix: "JNT_", section: "shipping", group: "J&T Express" },
  { prefix: "SHIPBLU_", section: "shipping", group: "ShipBlu" },
  { prefix: "YFS_", section: "shipping", group: "Yalla Fel Sekka" },
  { prefix: "ARAMEX_", section: "shipping", group: "Aramex" },
  { prefix: "MYLERZ_", section: "shipping", group: "Mylerz" },
  { prefix: "SPEEDAF_", section: "shipping", group: "Speedaf" },

  { prefix: "PAYMOB_", section: "payments", group: "Paymob" },
  { prefix: "PAYTABS_", section: "payments", group: "PayTabs" },
  { prefix: "KASHIER_", section: "payments", group: "Kashier" },
  { prefix: "FAWRY_", section: "payments", group: "Fawry" },
  { prefix: "STRIPE_", section: "payments", group: "Stripe" },
  { prefix: "TAP_", section: "payments", group: "Tap Payments" },
  { prefix: "EASYKASH_", section: "payments", group: "EasyKash" },

  { prefix: "WHATSAPP_", section: "messaging", group: "WhatsApp" },
  { prefix: "FCM_", section: "messaging", group: "Firebase (FCM)" },

  { prefix: "GEMINI_", section: "ai", group: "Gemini" },
  { prefix: "TRUST_PROXY_", section: "system", group: "Reverse proxy" },
  // Paid-services ledger caps for Google Routes (WhatsApp's and Gemini's
  // caps land with their own brand groups above).
  { prefix: "ROUTES_", section: "system", group: "Google Routes" },
];

// Order keys land in the orders section but share no prefix, so they're matched
// by substring rather than a rule (IS_ORDER_… doesn't start with ORDER_).
const ORDER_KEY_MARKER = "ORDER_";

type Placement = { section: SectionId; group: string | null };

export function placementOf(config: AppConfigView): Placement {
  if (config.origin === "custom") return { section: "custom", group: null };

  const rule = PREFIX_RULES.find((r) => config.key.startsWith(r.prefix));
  if (rule) return { section: rule.section, group: rule.group };

  if (config.key.includes(ORDER_KEY_MARKER)) {
    return { section: "orders", group: null };
  }
  return { section: "system", group: null };
}

export type ConfigGroup = {
  // null = the section renders its rows without a group heading.
  readonly label: string | null;
  readonly items: readonly AppConfigView[];
};

export type ConfigSection = {
  readonly id: SectionId;
  readonly groups: readonly ConfigGroup[];
  readonly count: number;
};

/**
 * Buckets configs into sections → groups, preserving SECTION_ORDER and, within
 * a section, PREFIX_RULES order. Empty sections and empty groups are dropped so
 * the page renders only what the current filter matched.
 */
export function groupConfigs(
  configs: readonly AppConfigView[],
): readonly ConfigSection[] {
  const bySection = new Map<SectionId, Map<string, AppConfigView[]>>();

  for (const config of configs) {
    const { section, group } = placementOf(config);
    const groups = bySection.get(section) ?? new Map<string, AppConfigView[]>();
    // "" is the sentinel bucket for rows that render without a heading.
    const bucket = group ?? "";
    groups.set(bucket, [...(groups.get(bucket) ?? []), config]);
    bySection.set(section, groups);
  }

  // Brand groups render in PREFIX_RULES order; the unlabelled bucket first.
  const groupRank = new Map(PREFIX_RULES.map((r, i) => [r.group, i]));

  return SECTION_ORDER.flatMap((id) => {
    const groups = bySection.get(id);
    if (!groups) return [];
    const ordered = [...groups.entries()]
      .sort(
        ([a], [b]) =>
          (a === "" ? -1 : (groupRank.get(a) ?? 0)) -
          (b === "" ? -1 : (groupRank.get(b) ?? 0)),
      )
      .map(([label, items]) => ({ label: label === "" ? null : label, items }));
    return [
      {
        id,
        groups: ordered,
        count: ordered.reduce((sum, g) => sum + g.items.length, 0),
      },
    ];
  });
}
